import React, { useState, useRef } from 'react';
import { List } from '../../core/List';
import { Card } from '../../core/Card';
import { Alert } from '../../core/Alert';
import { Confirmation } from '../../core/Popup';
import { ApiDataAccess } from '../../infrastructure/ApiDataAccess';

import "./SkusIntegrity.css";

export const FileCountIssues = () => {

    const confirmation = useRef(null);

    const [loading, setLoading] = useState(false);
    const [checked, setChecked] = useState(false);
    const [results, setResults] = useState(null);

    const [cleaning, setCleaning] = useState(false);
    const [cleaned, setCleaned] = useState(false);
    const [cleanupResults, setCleanupResults] = useState(null);

    const checkFileCountIssues = async () => {
        setLoading(true);
        try {
            var dataAccess = new ApiDataAccess('/api/esko/health/sku');
            var data = await dataAccess.get('/file-count-issues');
            setResults(data);
        }
        catch (ex) {
            setResults(null);
        }
        finally {
            setChecked(true);
            setLoading(false);
        }
    };

    const onCleanupDuplicatesClick = () => {
        confirmation.current.show(
            'Folders with exactly two files: the older file is deleted, keeping only the most recent one. ' +
            'Folders with more than two files: the whole SKU is considered unrecoverable - all its files are deleted from ' +
            'storage and the SKU itself (sku_property, sku_artwork, sku_info) is removed. This cannot be undone.',
            'Clean Up Duplicate Files',
            async () => { await cleanupDuplicates(); }
        );
    };

    const cleanupDuplicates = async () => {
        setCleaning(true);
        try {
            var dataAccess = new ApiDataAccess('/api/esko/health/sku');
            var data = await dataAccess.post('/cleanup-duplicates');
            setCleanupResults(data);
        }
        catch (ex) {
            setCleanupResults(null);
        }
        finally {
            setCleaned(true);
            setCleaning(false);
        }
    };

    return (
        <Card title='Health' subtitle='Check for folders in storage with anything other than exactly one file'>
            <div className="skus-health">
                <button
                    className='btn btn-square btn-tertiary-outline'
                    disabled={loading}
                    onClick={checkFileCountIssues}>
                    {loading ? 'Checking...' : 'Check File Counts'}
                </button>

                <button
                    className='btn btn-square btn-tertiary-outline'
                    disabled={cleaning}
                    onClick={onCleanupDuplicatesClick}>
                    {cleaning ? 'Cleaning up...' : 'Clean Up Duplicate Files'}
                </button>

                {cleaned && cleanupResults &&
                    cleanupResults.duplicatesResolved.length === 0 &&
                    cleanupResults.skusInvalidated.length === 0 &&
                    <Alert type={'info'}>No folder had two or more files, nothing was changed.</Alert>
                }

                {cleaned && cleanupResults && cleanupResults.duplicatesResolved.length > 0 &&
                    <div>
                        <Alert type={'success'}>{cleanupResults.duplicatesResolved.length} folder(s) with exactly 2 files cleaned up.</Alert>
                        <List
                            items={cleanupResults.duplicatesResolved}
                            headers={[
                                { column: 'globalOrderId', title: 'Order Number', sortable: true },
                                { column: 'skuId', title: 'Sku Number', sortable: true },
                                { column: 'fileType', title: 'File Type', sortable: true },
                                { column: 'deletedFileName', title: 'Deleted (oldest)', sortable: false },
                                { column: 'keptFileName', title: 'Kept (newest)', sortable: false }
                            ]}
                        />
                    </div>
                }

                {cleaned && cleanupResults && cleanupResults.skusInvalidated.length > 0 &&
                    <div>
                        <Alert type={'warn'}>{cleanupResults.skusInvalidated.length} sku(s) with more than 2 files were deleted.</Alert>
                        <List
                            items={cleanupResults.skusInvalidated}
                            headers={[
                                { column: 'globalOrderId', title: 'Order Number', sortable: true },
                                { column: 'skuId', title: 'Sku Number', sortable: true },
                                { column: 'reason', title: 'Reason', sortable: false }
                            ]}
                        />
                    </div>
                }

                {cleaned && !cleanupResults &&
                    <Alert type={'error'}>Failed to clean up duplicate files. Please try again later.</Alert>
                }

                {checked && results && results.length === 0 &&
                    <Alert type={'success'}>No folders with a missing or duplicate file were found.</Alert>
                }

                {checked && results && results.length > 0 &&
                    <List
                        items={results}
                        headers={[
                            { column: 'globalOrderId', title: 'Order Number', sortable: true },
                            { column: 'skuId', title: 'Sku Number', sortable: true },
                            { column: 'fileType', title: 'File Type', sortable: true },
                            { column: 'fileNames', title: 'Files', sortable: false },
                            { column: 'error', title: 'Error', sortable: false }
                        ]}
                        onRenderRow={(column, item, header) => {
                            switch (column) {
                                case 'fileNames':
                                    return (<span>{item.fileNames.length > 0 ? item.fileNames.join(', ') : '-'}</span>)
                                default:
                                    if (item[column] != null) {
                                        return (<span>{item[column]}</span>);
                                    }
                            }
                        }}
                    />
                }

                {checked && !results &&
                    <Alert type={'error'}>Failed to check file counts. Please try again later.</Alert>
                }
            </div>

            <Confirmation ref={confirmation} />
        </Card>
    )
}
